import React, { useState, useEffect, useRef } from 'react';
import { Scan, CheckCircle, XCircle, RotateCcw, Box, AlertCircle, X, Trash2, Truck, Receipt } from 'lucide-react';

// Dummy data for packages
const dummyPackages = [
  { id: 'UPS123456', carrier: 'UPS', destination: 'USA', service: 'Standard' },
  { id: 'UPS789012', carrier: 'UPS', destination: 'Canada', service: 'Express' },
  { id: 'DX345678SE', carrier: 'DanX', destination: 'Sweden', service: 'Standard' },
  { id: 'DX901234SE', carrier: 'DanX', destination: 'Sweden', service: 'Express' },
  { id: 'DX567890FI', carrier: 'DanX', destination: 'Finland', service: 'Standard' },
  { id: 'DX123456FI', carrier: 'DanX', destination: 'Finland', service: 'Special' },
  { id: 'DX789012NO', carrier: 'DanX', destination: 'Norway', service: 'Standard' },
  { id: 'DX345678DK', carrier: 'DanX', destination: 'Denmark', service: 'Express' },
  { id: 'FX456789', carrier: 'FedEx', destination: 'USA', service: 'Standard' },
  { id: 'DHL789012', carrier: 'DHL', destination: 'Germany', service: 'Express' }
];

// Define transport cages and their rules
const transportCages = [
  { 
    id: 'UPS', 
    name: 'UPS Cage',
    description: 'For all UPS packages',
    rule: (pkg) => pkg.carrier === 'UPS'
  },
  { 
    id: 'DanXSweden', 
    name: 'DanX Sweden',
    description: 'For all DanX packages to Sweden',
    rule: (pkg) => pkg.carrier === 'DanX' && pkg.destination === 'Sweden'
  },
  { 
    id: 'DanXFinlandSpecial', 
    name: 'DanX Finland Special Service',
    description: 'For DanX Special Service packages to Finland',
    rule: (pkg) => pkg.carrier === 'DanX' && pkg.destination === 'Finland' && pkg.service === 'Special'
  },
  { 
    id: 'DanXOther', 
    name: 'DanX Other',
    description: 'For all other DanX packages',
    rule: (pkg) => pkg.carrier === 'DanX' && !(
      (pkg.destination === 'Sweden') ||
      (pkg.destination === 'Finland' && pkg.service === 'Special')
    )
  },
  { 
    id: 'OtherCarriers', 
    name: 'Other Carriers',
    description: 'For all non-UPS, non-DanX packages',
    rule: (pkg) => pkg.carrier !== 'UPS' && pkg.carrier !== 'DanX'
  }
];

// Custom notification component centered on screen
const Notification = ({ type, message, onClose }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
      <div className={`flex items-center p-4 rounded-md shadow-lg max-w-md ${
        type === 'error' ? 'bg-red-100 border-l-4 border-red-500' : 
        type === 'success' ? 'bg-green-100 border-l-4 border-green-500' : 
        'bg-blue-100 border-l-4 border-blue-500'
      }`}>
        <div className="mr-3">
          {type === 'error' ? (
            <AlertCircle className="text-red-500" />
          ) : type === 'success' ? (
            <CheckCircle className="text-green-500" />
          ) : (
            <AlertCircle className="text-blue-500" />
          )}
        </div>
        <div className="mr-2">
          <div className="font-medium">
            {type === 'error' ? 'Error' : type === 'success' ? 'Success' : 'Info'}
          </div>
          <div className="text-sm">{message}</div>
        </div>
        <button 
          onClick={onClose} 
          className="ml-auto text-gray-500 hover:text-gray-700"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

const PackageScannerApp = () => {
  const [packageInput, setPackageInput] = useState('');
  const [cageInput, setCageInput] = useState('');
  const [scannedPackage, setScannedPackage] = useState(null);
  const [scannedCage, setScannedCage] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [scanStep, setScanStep] = useState('package'); // 'package' or 'cage'
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('scan');
  const [cageContents, setCageContents] = useState({});
  const packageInputRef = useRef(null);
  const cageInputRef = useRef(null);
  const successSoundRef = useRef(null);
  const errorSoundRef = useRef(null);

  // Initialize cage contents on component mount
  useEffect(() => {
    const initialCageContents = {};
    transportCages.forEach(cage => {
      initialCageContents[cage.id] = [];
    });
    setCageContents(initialCageContents);
  }, []);

  // Function to show notification
  const showNotification = (type, message) => {
    setNotification({ type, message });
    
    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  // Function to play sound
  const playSound = (type) => {
    if (type === 'success' && successSoundRef.current) {
      successSoundRef.current.currentTime = 0;
      successSoundRef.current.play().catch(e => console.error("Error playing sound:", e));
    } else if (type === 'error' && errorSoundRef.current) {
      errorSoundRef.current.currentTime = 0;
      errorSoundRef.current.play().catch(e => console.error("Error playing sound:", e));
    }
  };

  // Handle package input keypress
  const handlePackageInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const packageId = packageInput.trim();
      
      if (!packageId) {
        showNotification('error', 'Please enter a package barcode');
        playSound('error');
        return;
      }
      
      // Find the package in our dummy data
      const foundPackage = dummyPackages.find(pkg => pkg.id === packageId);
      
      if (!foundPackage) {
        showNotification('error', `Package ${packageId} was not found in the database. Please check your entry and try again.`);
        playSound('error');
        setPackageInput('');
        return;
      }
      
      // Check if the package has already been scanned into any cage
      const isAlreadyScanned = Object.values(cageContents).some(
        cagePackages => cagePackages.some(pkg => pkg.id === foundPackage.id)
      );
      
      if (isAlreadyScanned) {
        // Find which cage contains the package
        const cageId = Object.keys(cageContents).find(
          cageId => cageContents[cageId].some(pkg => pkg.id === foundPackage.id)
        );
        const cageName = transportCages.find(cage => cage.id === cageId)?.name;
        
        showNotification('error', `Package ${packageId} has already been scanned into ${cageName}. Cannot scan the same package twice.`);
        playSound('error');
        setPackageInput('');
        return;
      }
      
      setScannedPackage(foundPackage);
      setScanStep('cage');
      setCageInput('');
      setScanResult(null);
      playSound('success');
      
      // Focus the cage input after successfully scanning a package
      setTimeout(() => {
        if (cageInputRef.current) {
          cageInputRef.current.focus();
        }
      }, 50);
    }
  };
  
  // Handle cage input keypress
  const handleCageInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const cageId = cageInput.trim();
      
      if (!scannedPackage) {
        showNotification('error', 'Please scan a package first');
        playSound('error');
        setScanStep('package');
        setTimeout(() => {
          if (packageInputRef.current) {
            packageInputRef.current.focus();
          }
        }, 50);
        return;
      }
      
      if (!cageId) {
        showNotification('error', 'Please enter a cage barcode');
        playSound('error');
        return;
      }
      
      // Find the cage in our defined cages
      const foundCage = transportCages.find(cage => cage.id === cageId);
      
      if (!foundCage) {
        showNotification('error', `Cage ${cageId} was not found in the database. Please check your entry and try again.`);
        playSound('error');
        setCageInput('');
        return;
      }
      
      setScannedCage(foundCage);
      
      // Check if the package belongs to the scanned cage
      const isCorrect = foundCage.rule(scannedPackage);
      
      // Set the scan result
      setScanResult({
        isCorrect,
        timestamp: new Date()
      });
      
      // Play appropriate sound and show notification
      if (isCorrect) {
        playSound('success');
        //showNotification('success', `Package correctly placed in ${foundCage.name}`);
        
        // Add package to cage contents
        setCageContents(prev => ({
          ...prev,
          [foundCage.id]: [...prev[foundCage.id], {
            ...scannedPackage,
            scannedAt: new Date()
          }]
        }));
      } else {
        playSound('error');
        // Find the correct cage
        const correctCage = transportCages.find(cage => cage.rule(scannedPackage));
        showNotification('error', `Incorrect cage! This package belongs in the ${correctCage ? correctCage.name : 'Unknown'} cage.`);
      }
      
      // Add to scan history
      setScanHistory(prev => [
        {
          packageId: scannedPackage.id,
          cageId: foundCage.id,
          isCorrect,
          timestamp: new Date()
        },
        ...prev
      ].slice(0, 10)); // Keep only last 10 items
      
      // After showing the result, reset for the next package
      setTimeout(() => {
        resetScan();
      }, 2000);
    }
  };

    // Function to clear scan history
    const clearHistory = () => {
      setScanHistory([]);
      showNotification('info', 'Scan history has been cleared');
    };
  
  // Function to remove a package from a cage
  const removePackageFromCage = (cageId, packageId) => {
    setCageContents(prev => ({
      ...prev,
      [cageId]: prev[cageId].filter(pkg => pkg.id !== packageId)
    }));
    
    showNotification('info', `Package ${packageId} removed from cage`);
  };

  // These are kept for the form submit handlers
  const handlePackageScan = (e) => {
    e.preventDefault();
    // The actual scanning is handled by the keypress handler
  };
  
  const handleCageScan = (e) => {
    e.preventDefault();
    // The actual scanning is handled by the keypress handler
  };

  // Function to reset the scan
  const resetScan = () => {
    setPackageInput('');
    setCageInput('');
    setScannedPackage(null);
    setScannedCage(null);
    setScanResult(null);
    setScanStep('package');
    
    // Focus back on package input
    setTimeout(() => {
      if (packageInputRef.current) {
        packageInputRef.current.focus();
      }
    }, 50);
  };

  // Function to get status message
  const getStatusMessage = () => {
    if (!scannedPackage) return 'Please scan a package barcode';
    if (!scannedCage) return 'Now scan a cage barcode';
    
    if (scanResult.isCorrect) {
      return `Correct! This package belongs in the ${scannedCage.name}.`;
    } else {
      // Find the correct cage for this package
      const correctCage = transportCages.find(cage => cage.rule(scannedPackage));
      return `Incorrect! This package belongs in the ${correctCage ? correctCage.name : 'Unknown'} cage.`;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
<header className="text-white p-4" style={{backgroundColor: '#FFB600'}}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
          <img 
        src="/stryker-logo.png" 
        alt="Stryker Logo" 
        className="h-8 mr-3" 
      />
    
            <h1 className="text-xl font-bold">Cage Scanner</h1>
          </div>
        </div>
      </header>
      
      <main className="flex-grow p-4 md:p-6">
        {/* Tabs Navigation */}
        <div className="mb-6">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              className={`py-2 px-4 font-medium text-sm ${
                activeTab === 'scan'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('scan')}
            >
              Scanner
            </button>
            {transportCages.map(cage => (
              <button
                key={cage.id}
                className={`py-2 px-4 font-medium text-sm ${
                  activeTab === cage.id
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab(cage.id)}
              >
                {cage.name} ({cageContents[cage.id]?.length || 0})
              </button>
            ))}
          </div>
        </div>
        
        {/* Scanner Tab Content */}
        {activeTab === 'scan' ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Main Scanning Area */}
            <div className="md:col-span-3 bg-white rounded-lg shadow p-4">
              <div className="flex flex-col items-center justify-center">
                <div className={`mb-6 p-6 border-2 rounded-lg w-full max-w-md ${
                  scanResult 
                    ? (scanResult.isCorrect ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50')
                    : 'border-gray-300'
                }`}>
                  {/* Scan Package Form */}
                  <form onSubmit={handlePackageScan} className={`mb-4 ${scanStep !== 'package' ? 'opacity-50' : ''}`}>
                    <div className="text-lg font-medium mb-2 text-left">1. Scan Package</div>
                    <div className="flex">
                      <input
                        ref={packageInputRef}
                        type="text"
                        value={packageInput}
                        onChange={(e) => setPackageInput(e.target.value)}
                        onKeyDown={handlePackageInputKeyPress}
                        placeholder="Enter package barcode..."
                        className="flex-grow p-2 border rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={scanStep !== 'package'}
                        autoFocus
                      />
                      <button
                        type="submit"
                        className="bg-blue-600 text-white p-2 rounded-r-md hover:bg-blue-700 disabled:bg-gray-300"
                        disabled={scanStep !== 'package'}
                      >
                        <Scan size={20} />
                      </button>
                    </div>
                  </form>
                  
                  {/* Scan Cage Form */}
                  <form onSubmit={handleCageScan} className={`mb-4 ${scanStep !== 'cage' ? 'opacity-50' : ''}`}>
                    <div className="text-lg font-medium mb-2 text-left">2. Scan Cage</div>
                    <div className="flex">
                      <input
                        ref={cageInputRef}
                        type="text"
                        value={cageInput}
                        onChange={(e) => setCageInput(e.target.value)}
                        onKeyDown={handleCageInputKeyPress}
                        placeholder="Enter cage barcode..."
                        className="flex-grow p-2 border rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={scanStep !== 'cage'}
                      />
                      <button
                        type="submit"
                        className="bg-blue-600 text-white p-2 rounded-r-md hover:bg-blue-700 disabled:bg-gray-300"
                        disabled={scanStep !== 'cage'}
                      >
                        <Scan size={20} />
                      </button>
                    </div>
                  </form>
                  
                  {/* Package Details */}
                  {scannedPackage && (
                    <div className="bg-white p-4 rounded-md border mb-4">
                      <h3 className="font-bold text-lg mb-2">Package: {scannedPackage.id}</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-left text-gray-500">Carrier:</div>
                        <div className="text-right font-medium">{scannedPackage.carrier}</div>
                        <div className="text-left text-gray-500">Destination:</div>
                        <div className="text-right font-medium">{scannedPackage.destination}</div>
                        <div className="text-left text-gray-500">Service:</div>
                        <div className="text-right font-medium">{scannedPackage.service}</div>
                      </div>
                    </div>
                  )}
                  
                  {/* Cage Details */}
                  {scannedCage && (
                    <div className="bg-white p-4 rounded-md border mb-4">
                      <h3 className="font-bold text-lg mb-2">Cage: {scannedCage.name}</h3>
                      <div className="text-sm text-gray-500">{scannedCage.description} </div>
               

                    </div>
                  )}
                  
                  {/* Scan Result */}
                  {scanResult && (
                    <div className={`flex items-center justify-center text-lg font-medium ${
                      scanResult.isCorrect ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {scanResult.isCorrect 
                        ? <CheckCircle className="mr-2" size={24} /> 
                        : <XCircle className="mr-2" size={24} />}
                      {scanResult.isCorrect ? 'CORRECT CAGE' : 'WRONG CAGE'}
                    </div>
                  )}
                </div>
                
                <div className="text-center mb-6">
                  <div className="text-lg font-medium mb-1">Status</div>
                  <div className="text-gray-600">{getStatusMessage()}</div>
                </div>
                
                <div className="flex space-x-4">
                  <button
                    onClick={resetScan}
                    className="flex items-center px-6 py-3 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
                  >
                    <RotateCcw className="mr-2" size={20} />
                    Reset
                  </button>
                </div>
              </div>
            </div>
            
            {/* Right sidebar - Scan History */}
            <div className="md:col-span-1 bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Box className="mr-2 text-blue-600" size={20} />
                Scan History
              </h2>
              <button
                  onClick={clearHistory}
                  className="text-sm px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center"
                  disabled={scanHistory.length === 0}
                >
              <Trash2 className="mr-1" size={14} />
                  Clear
                </button>
              {scanHistory.length === 0 ? (
                <div className="text-center text-gray-500 py-6">
                  No scan history yet
                </div>
              ) : (
                <div className="space-y-2">
                  {scanHistory.map((scan, index) => (
                    <div 
                      key={index} 
                      className={`p-3 rounded-md border ${
                        scan.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium">
                          {scan.packageId}
                        </div>
                        {scan.isCorrect 
                          ? <CheckCircle className="text-green-600" size={16} /> 
                          : <XCircle className="text-red-600" size={16} />}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {scan.timestamp.toLocaleTimeString()} - {transportCages.find(c => c.id === scan.cageId)?.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Cage Content Tab */
          <div className="bg-white rounded-lg shadow p-4">
            {transportCages.map(cage => (
              activeTab === cage.id && (
                <div key={cage.id}>
                  <div className="flex items-center mb-4">
                    <Truck className="mr-2 text-blue-600" size={24} />
                    <h2 className="text-xl font-bold">{cage.name}</h2>
                  </div>
                  <p className="text-gray-600 mb-4">{cage.description}</p>
                  <Receipt className="mr-2 text-green-600" >
                  <button
                        onClick={() => removePackageFromCage(scannedCage.id, scannedPackage.id)}
                        className="mt-2 text-red-600 hover:text-red-800 text-sm"
                      >
                        
                 
                        Close Cage
                      </button>
                  </Receipt>
                  {cageContents[cage.id]?.length === 0 ? (
                    <div className="text-center text-gray-500 py-6 border rounded-md">
                      No packages in this cage yet
                    </div>
                  ) : (
                    <div className="overflow-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Package ID
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Carrier
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Destination
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Service
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Scanned Time
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {cageContents[cage.id].map((pkg) => (
                            <tr key={pkg.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {pkg.id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {pkg.carrier}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {pkg.destination}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {pkg.service}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {pkg.scannedAt.toLocaleTimeString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <button
                                  onClick={() => removePackageFromCage(cage.id, pkg.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            ))}
          </div>
        )}
      </main>
      
      <footer className="bg-gray-800 text-white p-4 text-center text-sm">
        Package Transport Scanner Demo - {new Date().getFullYear()}
      </footer>
      
      {/* Notification component */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      
      {/* Sound effects */}
      <audio ref={successSoundRef} preload="auto">
        <source src="https://cdn.pixabay.com/download/audio/2021/08/04/audio_0625c1539c.mp3?filename=success-1-6297.mp3" type="audio/mpeg" />
      </audio>
      <audio ref={errorSoundRef} preload="auto">
        <source src="https://cdn.pixabay.com/download/audio/2021/08/04/audio_c518b5af42.mp3?filename=error-126627.mp3" type="audio/mpeg" />
      </audio>
    </div>
  );
};

export default PackageScannerApp;